/**
 * Stato del consenso cookie/analytics, persistito in localStorage.
 *
 * Stesso pattern di useThemeStore (src/providers/ThemeProvider.tsx):
 * Zustand + middleware `persist`. La categoria "necessari" è implicita
 * (sempre attiva, non è una scelta dell'utente), quindi non la salviamo:
 * persistiamo solo `decided` (ha già scelto?) e `analytics` (categoria
 * opzionale on/off).
 *
 * `prefsOpen` è stato UI volatile (apertura del dialog preferenze): NON va
 * persistito — serve solo a permettere di riaprire il dialog da fuori il
 * banner (es. dalla pagina Privacy → "Gestisci preferenze cookie"), così la
 * scelta è revocabile/modificabile in ogni momento.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type ConsentState = {
  /** L'utente ha già espresso una scelta? Se false, mostriamo il banner. */
  decided: boolean
  /** Consenso alla categoria analitici (Google Analytics). */
  analytics: boolean
  /** Dialog preferenze aperto? (volatile, non persistito) */
  prefsOpen: boolean
  /** Registra la scelta dell'utente (segna anche `decided = true`). */
  setConsent: (analytics: boolean) => void
  /** Apre/chiude il dialog preferenze (riapribile dopo la scelta). */
  openPreferences: () => void
  setPrefsOpen: (open: boolean) => void
}

export const useConsentStore = create<ConsentState>()(
  persist(
    (set) => ({
      decided: false,
      analytics: false,
      prefsOpen: false,
      setConsent: (analytics) => set({ analytics, decided: true }),
      openPreferences: () => set({ prefsOpen: true }),
      setPrefsOpen: (prefsOpen) => set({ prefsOpen }),
    }),
    {
      name: 'enermed-consent',
      // Persisti solo la scelta, non lo stato UI del dialog.
      partialize: (s) => ({ decided: s.decided, analytics: s.analytics }),
    },
  ),
)
